/**
 * ═══════════════════════════════════════════════════════════════
 * NUTRIOP READER OFFER ENGINE — v1.1 (01 AUG 2026)
 * ═══════════════════════════════════════════════════════════════
 *
 * Powers one-click article offers (e.g. NIGHTCREW50 Spermidine + NMN stack):
 *   1. NMN product selector: swaps image / name / locale-correct pricing
 *   2. Primary CTA: cart-aware add (Spermidine + selected NMN, no duplicates),
 *      applies the discount code, VERIFIES it actually allocated, then
 *      redirects to cart. On failure: inline message, no blind redirect.
 *   3. Secondary CTA: cart-aware Spermidine-only add, no code, redirect.
 *   4. Cart page: one-time success banner confirming the code applied.
 *
 * v1.1: defends against the country-enforcer's document-level capture-phase
 * click interceptor, which force-navigates internal links lacking "country="
 * (beating any element-level preventDefault). CTA hrefs are rewritten at init
 * to the cart URL with country pre-attached, so the enforcer stands down; the
 * discount is also queued on the session at click-start so even a raced
 * navigation lands on the cart with products + code pending.
 *
 * Loaded via theme.liquid, gated to article + cart templates.
 *
 * ARTICLE BLOCK CONTRACT (all behavior is data-driven, no per-offer JS edits):
 *   #ntr-offer            root element
 *     data-ntr-code       discount code (e.g. "NIGHTCREW50")
 *     data-ntr-anchor     anchor product handle (the "customer buys" item)
 *     data-ntr-txt-*      optional localized strings (see TXT defaults below)
 *   #ntr-nmn-select       <select> of eligible handles (option value = handle)
 *   #ntr-nmn-image        product image, swapped on selection
 *   #ntr-nmn-name         product name line, swapped on selection
 *   #ntr-nmn-price        price line: full price struck → 50% price
 *   #ntr-cta-stack        primary CTA anchor
 *   #ntr-cta-solo         secondary CTA anchor
 *   #ntr-status           inline status / error area
 *
 * Locale-aware throughout via Shopify.routes.root (works under /de-ch/,
 * /en-eu/, /no-ch/, etc.) — product prices come from the market-correct
 * /products/{handle}.js endpoint, never from hardcoded values.
 */
(function () {
  'use strict';

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var SS_KEY = 'ntrReaderOffer';

  /* ───────────────────────── helpers ───────────────────────── */

  function getJSON(url) {
    return fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status + ' for ' + url);
      return r.json();
    });
  }

  function getCart() {
    return getJSON(ROOT + 'cart.js?t=' + Date.now());
  }

  /* Country for the enforcer: NutriopLocale public API first, URL param second. */
  function detectCountry() {
    if (window.NutriopLocale && window.NutriopLocale.country) {
      return String(window.NutriopLocale.country).toUpperCase();
    }
    var m = window.location.search.match(/[?&]country=([A-Za-z]{2})/);
    return m ? m[1].toUpperCase() : null;
  }

  /* Cart URL carrying "country=" so the country enforcer's click interceptor
     stands down (it skips links that already have the param). */
  function cartUrl() {
    var country = detectCountry();
    return ROOT + 'cart' + (country ? '?country=' + country : '');
  }

  var productCache = {};
  function getProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = getJSON(ROOT + 'products/' + encodeURIComponent(handle) + '.js')
        .catch(function (err) {
          delete productCache[handle];
          throw err;
        });
    }
    return productCache[handle];
  }

  /* Currency formatting per Nutriop convention: CHF prepends, EUR appends. */
  function fmt(cents) {
    var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    var amount = (cents / 100).toFixed(2);
    if (cur === 'CHF') return 'CHF ' + amount;
    if (cur === 'EUR') return amount + ' EUR';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: cur
      }).format(cents / 100);
    } catch (e) {
      return amount + ' ' + cur;
    }
  }

  function imgUrl(p, width) {
    var src = p.featured_image || (p.images && p.images[0]) || '';
    if (!src) return '';
    if (src.indexOf('//') === 0) src = 'https:' + src;
    if (width) src += (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
    return src;
  }

  function pickVariant(p) {
    if (!p || !p.variants || !p.variants.length) return null;
    for (var i = 0; i < p.variants.length; i++) {
      if (p.variants[i].available) return p.variants[i];
    }
    return null; /* nothing purchasable → treat as out of stock */
  }

  function cartLines(cart, handle) {
    return (cart.items || []).filter(function (i) { return i.handle === handle; });
  }

  /* Discount "customer buys" requires a ONE-TIME purchase line — a Seal
     subscription line (selling_plan_allocation present) does not qualify. */
  function hasOneTimeLine(cart, handle) {
    return cartLines(cart, handle).some(function (i) { return !i.selling_plan_allocation; });
  }

  function hasAnyLine(cart, handle) {
    return cartLines(cart, handle).length > 0;
  }

  function postCartAdd(items) {
    return fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      if (!r.ok) {
        return r.json().catch(function () { return {}; }).then(function (j) {
          var msg = (j && (j.description || j.message)) || null;
          throw { user: msg };
        });
      }
      return r.json();
    });
  }

  /* ─────────────────── discount apply + verify ─────────────────── */

  /**
   * Verification reads BOTH structures (lesson from SUMMER-TRIO):
   *  - line-level allocations (product / Buy X Get Y discounts live here)
   *  - cart-level applications (order discounts live here)
   * plus the discount_codes[].applicable flag where the shop exposes it.
   * "Applied" = allocation evidence with amount > 0, or applicable code
   * with a nonzero total_discount. A code merely attached but allocating
   * nothing is NOT a success.
   */
  function verifyDiscount(cart, code) {
    var CODE = String(code || '').toUpperCase();
    if (!CODE || !cart) return false;

    var lineOk = (cart.items || []).some(function (i) {
      var viaAllocations = (i.line_level_discount_allocations || []).some(function (al) {
        var t = (al.discount_application &&
                (al.discount_application.title || al.discount_application.key)) || '';
        return String(t).toUpperCase() === CODE && al.amount > 0;
      });
      var viaDiscounts = (i.discounts || []).some(function (d) {
        return String(d.title || '').toUpperCase() === CODE && d.amount > 0;
      });
      return viaAllocations || viaDiscounts;
    });

    var cartOk = (cart.cart_level_discount_applications || []).some(function (a) {
      return String(a.title || '').toUpperCase() === CODE;
    });

    var codeOk = (cart.discount_codes || []).some(function (c) {
      return String(c.code || '').toUpperCase() === CODE && c.applicable !== false;
    });

    return lineOk || cartOk || (codeOk && (cart.total_discount || 0) > 0);
  }

  /**
   * Primary: POST /cart/update.js with { discount } (returns the updated
   * cart for immediate verification). Fallback: the /discount/{code}
   * session route — same mechanism as the theme's cart discount input —
   * after which the caller re-fetches cart.js and verifies.
   * Resolves true only when the code is VERIFIED as allocating a discount.
   */
  function applyCode(code) {
    return fetch(ROOT + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: code })
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; })
      .then(function (cart) {
        if (cart && verifyDiscount(cart, code)) return true;
        /* Fallback: session route, then let the caller re-verify. */
        return fetch(ROOT + 'discount/' + encodeURIComponent(code), {
          credentials: 'same-origin',
          redirect: 'follow'
        }).then(function () { return false; }).catch(function () { return false; });
      });
  }

  /* ───────────────────── article offer block ───────────────────── */

  function initOffer(root) {
    var code = String(root.getAttribute('data-ntr-code') || '').toUpperCase();
    var anchorHandle = root.getAttribute('data-ntr-anchor') || '';
    if (!code || !anchorHandle) return;

    var select   = document.getElementById('ntr-nmn-select');
    var image    = document.getElementById('ntr-nmn-image');
    var nameEl   = document.getElementById('ntr-nmn-name');
    var priceEl  = document.getElementById('ntr-nmn-price');
    var ctaStack = document.getElementById('ntr-cta-stack');
    var ctaSolo  = document.getElementById('ntr-cta-solo');
    var statusEl = document.getElementById('ntr-status');

    function txt(key, fallback) {
      return root.getAttribute('data-ntr-txt-' + key) || fallback;
    }
    var TXT = {
      adding:     txt('adding', 'Adding your stack…'),
      added:      txt('added', 'Added ✓ — taking you to your cart…'),
      addingSolo: txt('adding-solo', 'Adding Pure Spermidine…'),
      addFail:    txt('add-fail', 'Something went wrong adding the products — please try again.'),
      codeFail:   txt('code-fail', 'Both products are in your cart, but code {code} could not be applied automatically — it may have expired or already been used. You can enter it manually at checkout.'),
      oos:        txt('oos', 'Temporarily out of stock'),
      viewCart:   txt('view-cart', 'View cart'),
      withCode:   txt('with-code', 'with code'),
      banner:     txt('banner', '{code} applied — 50% off your NMN product is reflected in your cart below.'),
      bannerSoft: txt('banner-soft', 'If you do not see your discount below, enter code {code} at checkout.')
    };

    var current = { handle: null, variantId: null, ok: false };

    function showStatus(html, kind) {
      if (!statusEl) return;
      statusEl.style.display = 'block';
      statusEl.style.background = kind === 'error' ? 'rgba(214,86,86,0.16)' : 'rgba(63,167,158,0.16)';
      statusEl.style.borderLeft = '4px solid ' + (kind === 'error' ? '#e08585' : '#3fa79e');
      statusEl.innerHTML = html;
    }
    function hideStatus() {
      if (statusEl) statusEl.style.display = 'none';
    }

    function setBusy(btn, label) {
      btn.setAttribute('data-orig', btn.innerHTML);
      btn.setAttribute('data-busy', '1');
      btn.innerHTML = label;
      btn.style.pointerEvents = 'none';
      btn.style.opacity = '0.75';
    }
    function clearBusy(btn) {
      var orig = btn.getAttribute('data-orig');
      if (orig) btn.innerHTML = orig;
      btn.removeAttribute('data-busy');
      btn.style.pointerEvents = '';
      btn.style.opacity = '';
    }

    function selectedLabel() {
      if (!select) return '';
      var o = select.options[select.selectedIndex];
      return o ? o.textContent.trim() : '';
    }

    /* Hydrate image / name / price for the currently selected NMN. */
    function refreshSelected() {
      if (!select) return;
      var handle = select.value;
      current = { handle: handle, variantId: null, ok: false };
      if (priceEl) priceEl.innerHTML = '&nbsp;';

      getProduct(handle).then(function (p) {
        if (select.value !== handle) return; /* selection changed mid-flight */

        var src = imgUrl(p, 420);
        if (image && src) {
          image.src = src;
          image.alt = p.title || image.alt;
        }
        if (nameEl) nameEl.textContent = selectedLabel() || p.title || '';

        var v = pickVariant(p);
        if (!v) {
          if (priceEl) {
            priceEl.innerHTML = '<span style="color:#e8a1a1;font-weight:700;">' + TXT.oos + '</span>';
          }
          if (ctaStack) {
            ctaStack.style.opacity = '0.55';
            ctaStack.setAttribute('aria-disabled', 'true');
          }
          return;
        }

        current.variantId = v.id;
        current.ok = true;
        if (ctaStack) {
          ctaStack.style.opacity = '';
          ctaStack.removeAttribute('aria-disabled');
        }
        if (priceEl) {
          var half = Math.round(v.price / 2);
          priceEl.innerHTML =
            '<span style="text-decoration:line-through;opacity:0.55;">' + fmt(v.price) + '</span>' +
            ' &nbsp;→&nbsp; <strong style="color:#f6d597;">' + fmt(half) + '</strong>' +
            ' <span style="opacity:0.7;">' + TXT.withCode + ' ' + code + '</span>';
        }
      }).catch(function (err) {
        if (priceEl) priceEl.innerHTML = '&nbsp;';
        console.error('[ntr-offer] product fetch failed:', err);
      });
    }

    /* PRIMARY: Spermidine + selected NMN + code, cart-aware, verified. */
    function claimStack(e) {
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      if (ctaStack.getAttribute('data-busy')) return;
      if (!current.ok || !current.variantId) { refreshSelected(); return; }
      hideStatus();
      setBusy(ctaStack, TXT.adding);

      /* Set the cart-banner payload NOW: the cart page live-verifies the code
         and picks the success vs. soft message itself, so this stays correct
         even if a third-party script races us to the cart mid-flow. Removed
         again on any failure path below. */
      try {
        sessionStorage.setItem(SS_KEY, JSON.stringify({
          code: code,
          banner: TXT.banner,
          bannerSoft: TXT.bannerSoft,
          ts: Date.now()
        }));
      } catch (err) { /* private mode */ }

      /* Queue the code on the session immediately (fire-and-forget): a raced
         navigation then still lands with the code pending at cart/checkout. */
      fetch(ROOT + 'discount/' + encodeURIComponent(code), {
        credentials: 'same-origin',
        redirect: 'follow'
      }).catch(function () {});

      Promise.all([getProduct(anchorHandle), getCart()])
        .then(function (res) {
          var anchorVariant = pickVariant(res[0]);
          var cart = res[1];
          if (!anchorVariant) throw { user: TXT.oos };

          var items = [];
          if (!hasOneTimeLine(cart, anchorHandle)) {
            items.push({ id: anchorVariant.id, quantity: 1 }); /* one-time, NO selling_plan */
          }
          if (!hasAnyLine(cart, current.handle)) {
            items.push({ id: current.variantId, quantity: 1 });
          }
          return items.length ? postCartAdd(items) : null;
        })
        .then(function () { return applyCode(code); })
        .then(function (applied) {
          if (applied) return true;
          /* Fallback path set a session code — verify against a fresh cart. */
          return getCart().then(function (c) { return verifyDiscount(c, code); });
        })
        .then(function (applied) {
          if (applied) {
            ctaStack.innerHTML = TXT.added;
            window.location.href = cartUrl();
          } else {
            try { sessionStorage.removeItem(SS_KEY); } catch (err) {}
            clearBusy(ctaStack);
            showStatus(
              TXT.codeFail.replace('{code}', '<strong style="letter-spacing:1px;">' + code + '</strong>') +
              ' &nbsp;<a href="' + ROOT + 'cart" style="color:#f6d597;font-weight:700;text-decoration:underline;">' +
              TXT.viewCart + ' →</a>',
              'error'
            );
          }
        })
        .catch(function (err) {
          try { sessionStorage.removeItem(SS_KEY); } catch (e2) {}
          clearBusy(ctaStack);
          showStatus((err && err.user) || TXT.addFail, 'error');
          if (!(err && err.user)) console.error('[ntr-offer]', err);
        });
    }

    /* SECONDARY: Spermidine only, cart-aware, no code, redirect. */
    function soloAdd(e) {
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      if (ctaSolo.getAttribute('data-busy')) return;
      hideStatus();
      setBusy(ctaSolo, TXT.addingSolo);

      Promise.all([getProduct(anchorHandle), getCart()])
        .then(function (res) {
          var anchorVariant = pickVariant(res[0]);
          var cart = res[1];
          if (!anchorVariant) throw { user: TXT.oos };
          if (hasAnyLine(cart, anchorHandle)) return null; /* already in cart */
          return postCartAdd([{ id: anchorVariant.id, quantity: 1 }]);
        })
        .then(function () {
          window.location.href = cartUrl();
        })
        .catch(function (err) {
          clearBusy(ctaSolo);
          showStatus((err && err.user) || TXT.addFail, 'error');
          if (!(err && err.user)) console.error('[ntr-offer]', err);
        });
    }

    /* DEFENSE vs. nutriop-country-enforcer: its document-level CAPTURE-phase
       click interceptor force-navigates any internal link missing "country=",
       running before this element-level handler and immune to preventDefault.
       Rewriting the hrefs to a pre-satisfied cart URL makes it stand down —
       and if any script ever does navigate to the raw href, the destination
       is now the cart (with the in-flight adds landing there), not the
       product page. Runs only when JS runs, so the no-JS product-page
       fallback href in the article markup is preserved. */
    if (ctaStack) ctaStack.setAttribute('href', cartUrl());
    if (ctaSolo) ctaSolo.setAttribute('href', cartUrl());

    if (select) select.addEventListener('change', refreshSelected);
    /* Capture phase + stopPropagation keeps bubble-phase document-level
       interceptors (loaders, trackers) out of these two clicks entirely. */
    if (ctaStack) ctaStack.addEventListener('click', claimStack, true);
    if (ctaSolo) ctaSolo.addEventListener('click', soloAdd, true);
    refreshSelected();
  }

  /* ─────────────────── cart page success banner ─────────────────── */

  function initCartBanner() {
    var raw = null;
    try { raw = sessionStorage.getItem(SS_KEY); } catch (e) { return; }
    if (!raw) return;
    try { sessionStorage.removeItem(SS_KEY); } catch (e) { /* show once */ }

    var data = null;
    try { data = JSON.parse(raw); } catch (e) { return; }
    if (!data || !data.code) return;
    if (Date.now() - (data.ts || 0) > 10 * 60 * 1000) return; /* stale */

    getCart().then(function (cart) {
      var ok = verifyDiscount(cart, data.code);
      var msg = ok ? data.banner : data.bannerSoft;
      if (!msg) return;

      var banner = document.createElement('div');
      banner.id = 'ntr-cart-banner';
      banner.setAttribute('role', 'status');
      banner.style.cssText =
        'margin:18px auto;max-width:960px;padding:14px 20px;border-radius:14px;' +
        'font-size:15px;line-height:1.5;font-weight:600;' +
        (ok
          ? 'background:linear-gradient(135deg,rgba(63,167,158,0.16),rgba(63,167,158,0.08));border:1px solid rgba(63,167,158,0.5);color:#1d6e66;'
          : 'background:rgba(232,169,60,0.14);border:1px solid rgba(232,169,60,0.55);color:#8a6414;');
      banner.innerHTML = (ok ? '✓ ' : '') +
        String(msg).replace('{code}', '<strong>' + data.code + '</strong>');

      var main = document.getElementById('MainContent') ||
                 document.querySelector('main') ||
                 document.body;
      main.insertBefore(banner, main.firstChild);
    }).catch(function () { /* banner is decorative — never block the cart */ });
  }

  /* ───────────────────────── bootstrap ───────────────────────── */

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  ready(function () {
    var offer = document.getElementById('ntr-offer');
    if (offer) initOffer(offer);

    var isCartPath = /^\/([a-z]{2}(-[a-z]{2})?\/)?cart\/?$/i.test(window.location.pathname);
    if (isCartPath || document.querySelector('cart-items')) initCartBanner();
  });
})();